import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";
import {
  GraficoAntiguedadPorEtapa,
  GraficoCreacionPorHora,
  GraficoDesfaseCreacion,
  GraficoDistribucionTiempos,
  GraficoDuracionPorRBD,
  GraficoImpactoPorCategoria,
  GraficoMapaCalor,
  GraficoMapaCalorPorcentaje,
  GraficoPermanenciaPorEtapa,
  GraficoPorCategoria,
  GraficoPorEstado,
  GraficoPorOperador,
  GraficoReferencia,
} from "./ReporteContingenciasGraficos";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 36 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

function etiquetaPeriodo(periodo) {
  if (!periodo) return "—";
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function numero(valor, decimales = 0) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

function porcentaje(valor) {
  if (valor == null) return "—";
  return `${numero(valor, 2)}%`;
}

async function describirError(e) {
  const data = e?.response?.data;
  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }
  if (detalle) return detalle;
  if (e?.response?.status === 403) return "No tienes privilegios para ver este reporte.";
  return "No se pudo generar el reporte. Intenta nuevamente.";
}

function Seccion({ titulo, nota, children }) {
  return (
    <Paper elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.15)", mb: 2.5 }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
      </Box>
      {children}
      {nota && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
            {nota}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function Tabla({ cabeceras, alineaciones = [], filas }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {cabeceras.map((c, i) => (
              <TableCell
                key={c}
                sx={{ fontWeight: 600 }}
                align={alineaciones[i] || "left"}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((fila, idx) => (
            <TableRow key={idx} hover>
              {fila.map((valor, i) => (
                <TableCell key={i} align={alineaciones[i] || "left"}>
                  {valor}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ReporteContingencias() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [periodo, setPeriodo] = useState(periodos[1] ?? periodos[0]);
  const [idOperador, setIdOperador] = useState("");

  const [operadores, setOperadores] = useState([]);
  const [reporte, setReporte] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPageTitle?.("REPORTES DE JUSTIFICACIONES DE INDISPONIBILIDAD");
  }, []);

  useEffect(() => {
    axios
      .getAllOperadores()
      .then((res) => setOperadores(res?.data ?? []))
      .catch(() => setOperadores([]));
  }, []);

  const parametros = () => ({
    periodo,
    id_operador: idOperador || undefined,
  });

  const consultar = async () => {
    setCargando(true);
    setError(null);
    setReporte(null);
    try {
      const res = await axios.getReporteContingencias(parametros());
      setReporte(res?.data ?? null);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setCargando(false);
    }
  };

  const descargarPDF = async () => {
    setDescargando(true);
    setError(null);
    try {
      const res = await axios.getReporteContingenciasPDF(parametros());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const sufijo = idOperador ? `_operador_${idOperador}` : "";
      link.setAttribute("download", `reporte_contingencias_${periodo}${sufijo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setDescargando(false);
    }
  };

  const resumen = reporte?.resumen;

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          REPORTES DE JUSTIFICACIONES DE INDISPONIBILIDAD
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Contingencias que afectan el período, con el mismo criterio de selección que usa
          la prefactura.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 220px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Período:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 300px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operador:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={idOperador}
              onChange={(e) => setIdOperador(e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Todos los operadores</MenuItem>
              {operadores.map((o) => (
                <MenuItem key={o.id_operador} value={o.id_operador}>
                  {o.nombre_operador}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                cargando ? <CircularProgress size={16} /> : <AssessmentOutlinedIcon />
              }
              disabled={cargando}
              onClick={consultar}
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              {cargando ? "Calculando..." : "Ver reporte"}
            </Button>
            <Button
              startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={cargando || descargando}
              onClick={descargarPDF}
              sx={{ textTransform: "none" }}
            >
              {descargando ? "Generando..." : "Descargar PDF"}
            </Button>
          </Box>
        </Box>

        {cargando && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Calculando métricas del período...</Typography>
          </Box>
        )}

        {!cargando && error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte?.aviso && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(237, 108, 2, 0.3)",
              background: "rgba(237, 108, 2, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#ED6C02" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#B35300" }}>
              {reporte.aviso}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte && (
          <PageBgBlock pad="0">
            <Seccion
              titulo={`Resumen — ${etiquetaPeriodo(reporte.periodo)}${
                reporte.nombre_operador ? ` — ${reporte.nombre_operador}` : ""
              }`}
              nota="Selección: ESPECIFICA/ZONAL con ocurrencia o resolución dentro del período; PROGRAMADA por solape. La duración se recorta a los límites del mes."
            >
              <Tabla
                cabeceras={["Indicador", "Valor"]}
                alineaciones={["left", "right"]}
                filas={[
                  ["Contingencias que afectan el período", numero(resumen.total)],
                  ["RBD afectados", numero(resumen.rbd_afectados)],
                  ["Zonas afectadas", numero(resumen.zonas_afectadas)],
                  ["Operadores involucrados", numero(resumen.operadores_involucrados)],
                  ["Cerradas (Aprobado / Rechazado)", numero(resumen.cerradas)],
                  ["En trámite", numero(resumen.en_tramite)],
                  ["Sin resolución registrada (abiertas)", numero(resumen.abiertas)],
                  ["Duración abarcada en el período (h reloj)", numero(resumen.duracion_abarcada_horas, 2)],
                  ["Impacto: duración × RBD afectados (h-RBD)", numero(resumen.impacto_rbd_horas, 2)],
                  ["Impacto efectivo en jornada (h-RBD)", numero(resumen.impacto_efectivo_rbd_horas, 2)],
                  ...(resumen.por_efecto ?? []).map((e) => [
                    `Resolución: ${e.efecto}`,
                    numero(e.total),
                  ]),
                ]}
              />
            </Seccion>

            {reporte.referencia && (
              <Seccion
                titulo="Impacto frente a las horas disponibles"
                nota="Las horas que esos mismos establecimientos tienen en el período, en las dos unidades del reporte. El impacto es una suma sobre eventos: si dos contingencias caen sobre el mismo establecimiento, sus horas se cuentan dos veces, así que el porcentaje puede pasar de 100."
              >
                <Tabla
                  cabeceras={["Indicador", "Valor"]}
                  alineaciones={["left", "right"]}
                  filas={[
                    ["Establecimientos alcanzados", numero(reporte.referencia.rbd)],
                    ["Con más de una contingencia", numero(reporte.referencia.rbd_con_varias_contingencias)],
                    ["Sin jornada registrada", numero(reporte.referencia.rbd_sin_jornada)],
                    ["Horas de calendario disponibles (h-RBD)", numero(reporte.referencia.horas_calendario, 2)],
                    ["Horas de jornada disponibles (h-RBD)", numero(reporte.referencia.horas_jornada, 2)],
                    ["Impacto bruto sobre el calendario", porcentaje(reporte.referencia.porcentaje_calendario)],
                    ["Impacto efectivo sobre la jornada", porcentaje(reporte.referencia.porcentaje_jornada)],
                  ]}
                />
                <GraficoReferencia reporte={reporte} />
              </Seccion>
            )}

            <Seccion
              titulo="Por tipo de contingencia"
              nota="Los cuatro tipos que ofrece el sistema al crear una justificación: el origen distingue EXTERNA de INTERNA, que es donde cambia el circuito de firmas. Duración: horas de reloj dentro del período. Impacto (h-RBD): esa duración por los establecimientos afectados. Impacto efectivo: solo los minutos dentro de la jornada de funcionamiento."
            >
              <Tabla
                cabeceras={["Tipo", "Total", "RBD", "Abiertas", "Rechaz.", "Duración (h)", "Promedio (h)", "Impacto (h-RBD)", "Efectivo (h-RBD)"]}
                alineaciones={["left", "right", "right", "right", "right", "right", "right", "right", "right"]}
                filas={(reporte.por_categoria ?? []).map((t) => [
                  t.categoria,
                  numero(t.total),
                  numero(t.rbd_afectados),
                  numero(t.abiertas),
                  numero(t.rechazadas),
                  numero(t.duracion_abarcada_horas, 2),
                  numero(t.duracion_promedio_horas, 2),
                  numero(t.impacto_rbd_horas, 2),
                  numero(t.impacto_efectivo_rbd_horas, 2),
                ])}
              />
              <GraficoPorCategoria reporte={reporte} />
              <GraficoImpactoPorCategoria reporte={reporte} />
            </Seccion>

            {(reporte.duracion_por_rbd ?? []).length > 0 && (
              <Seccion
                titulo="Duración que cubre cada justificación, por establecimiento"
                nota="Caja: primer a tercer cuartil, línea en la mediana, bigotes de Tukey y los puntos fuera de norma. Los totales no distinguen entre muchas justificaciones cortas y unas pocas larguísimas; la distribución sí."
              >
                <GraficoDuracionPorRBD reporte={reporte} />
              </Seccion>
            )}

            <Seccion
              titulo="Estados y etapas del circuito de firmas"
              nota="Resolución: en qué quedó la justificación. Aprobada exime el descuento y rechazada lo mantiene; todos los estados intermedios quedan como “En trámite”, que no es una resolución sino la ausencia de una. El cálculo de la prefactura corre después de los cierres, así que una justificación en trámite todavía no tiene efecto definido sobre el descuento. La antigüedad se mide desde la última transición registrada."
            >
              <Tabla
                cabeceras={["Estado", "Resolución", "Total", "RBD", "Antig. prom. (d)", "Antig. máx. (d)"]}
                alineaciones={["left", "left", "right", "right", "right", "right"]}
                filas={(reporte.por_estado ?? []).map((e) => [
                  e.estado,
                  e.efecto,
                  numero(e.total),
                  numero(e.rbd_afectados),
                  numero(e.antiguedad_promedio_dias, 2),
                  numero(e.antiguedad_maxima_dias, 2),
                ])}
              />
              <GraficoPorEstado reporte={reporte} />
              <GraficoAntiguedadPorEtapa reporte={reporte} />
            </Seccion>

            {(reporte.tiempos_por_etapa ?? []).some(
              (e) => e.permanencia?.mediana != null
            ) && (
              <Seccion
                titulo="Permanencia en el circuito de firmas"
                nota="Cuánto demoró cada etapa que ya fue superada, medida entre una transición del historial y la siguiente. No es lo mismo que la antigüedad de las que siguen detenidas."
              >
                <GraficoPermanenciaPorEtapa reporte={reporte} />
              </Seccion>
            )}

            <Seccion
              titulo="Tiempos del ciclo de vida"
              nota="Resolución y registro se miden solo sobre fallas (ESPECIFICA/ZONAL): una PROGRAMADA es un mantenimiento planificado y su duración no es un tiempo de respuesta."
            >
              <Tabla
                cabeceras={["Métrica", "Casos", "Promedio", "Mediana", "Máximo"]}
                alineaciones={["left", "right", "right", "right", "right"]}
                filas={[
                  ["Resolución: ocurrencia → cierre (h)", "mttr_horas"],
                  ["Registro: ocurrencia → creación (h)", "latencia_registro_horas"],
                  ["Tramitación: creación → cierre (d)", "tramitacion_dias"],
                  ["Antigüedad en la etapa actual (d)", "antiguedad_etapa_dias"],
                ].map(([etiqueta, clave]) => {
                  const m = reporte.tiempos?.[clave] ?? {};
                  return [
                    etiqueta,
                    numero(m.n),
                    numero(m.promedio, 2),
                    numero(m.mediana, 2),
                    numero(m.maximo, 2),
                  ];
                })}
              />
              <GraficoDistribucionTiempos reporte={reporte} />
            </Seccion>

            {(reporte.creacion_vs_evento ?? []).length > 0 && (
              <Seccion
                titulo="Días entre la creación del registro y el evento"
                nota="Positivo: la justificación se creó antes del hito. Una PROGRAMADA debería declararse antes de ejecutar el mantenimiento; una falla da negativo por naturaleza, porque primero ocurre y después se declara."
              >
                <GraficoDesfaseCreacion reporte={reporte} />
              </Seccion>
            )}

            {reporte.creacion_por_hora?.total > 0 && (
              <Seccion
                titulo="Hora de creación del registro"
                nota={`Hora de Chile (${
                  reporte.zona_horaria || "America/Santiago"
                }). La fecha de creación la escribe el servidor de base de datos en UTC, así que se convierte antes de leerla: sin esa conversión el corte queda corrido tres o cuatro horas —el desfase cambia con el horario de verano— y el peak aparece al final de la tarde cuando en realidad es de media tarde. El horario hábil no es la jornada de funcionamiento del establecimiento: es el horario de oficina contra el que se lee si la declaración se hizo en horario de trabajo.`}
              >
                <GraficoCreacionPorHora reporte={reporte} />
                <Tabla
                  cabeceras={["Indicador", "Valor"]}
                  alineaciones={["left", "right"]}
                  filas={[
                    ["Justificaciones con fecha de creación", numero(reporte.creacion_por_hora.total)],
                    [
                      "Hora con más creaciones",
                      reporte.creacion_por_hora.hora_peak == null
                        ? "—"
                        : `${String(reporte.creacion_por_hora.hora_peak).padStart(2, "0")}:00 h`,
                    ],
                    ["Justificaciones creadas en esa hora", numero(reporte.creacion_por_hora.peak)],
                    [
                      "Creadas en horario hábil (08:00-18:00)",
                      numero(reporte.creacion_por_hora.en_horario_habil),
                    ],
                    ["Porcentaje en horario hábil", porcentaje(reporte.creacion_por_hora.porcentaje_habil)],
                    ["Creadas fuera de horario hábil", numero(reporte.creacion_por_hora.fuera_de_horario)],
                    ["Sin fecha de creación registrada", numero(reporte.creacion_por_hora.sin_fecha)],
                  ]}
                />
                <Tabla
                  cabeceras={["Franja del día", "Justificaciones", "Porcentaje"]}
                  alineaciones={["left", "right", "right"]}
                  filas={(reporte.creacion_por_hora.tramos ?? []).map((t) => [
                    t.tramo,
                    numero(t.total),
                    porcentaje(t.porcentaje),
                  ])}
                />
                {(reporte.creacion_por_hora.por_categoria ?? []).length > 1 && (
                  <Tabla
                    cabeceras={["Tipo", "Total", "Hora peak", "En horario hábil", "% hábil", "Fuera de horario"]}
                    alineaciones={["left", "right", "right", "right", "right", "right"]}
                    filas={reporte.creacion_por_hora.por_categoria.map((c) => [
                      c.categoria,
                      numero(c.total),
                      c.hora_peak == null
                        ? "—"
                        : `${String(c.hora_peak).padStart(2, "0")}:00`,
                      numero(c.en_horario_habil),
                      porcentaje(c.porcentaje_habil),
                      numero(c.fuera_de_horario),
                    ])}
                  />
                )}
              </Seccion>
            )}

            <Seccion
              titulo="Por operador y comparativa con su parque"
              nota="RBD asignados: establecimientos del operador en servicios_conectividad vigentes durante el período."
            >
              <Tabla
                cabeceras={["Operador", "Cont.", "RBD afectados", "RBD asignados", "% parque", "Duración (h)", "MTTR (h)", "En trámite", "Rechazadas"]}
                alineaciones={["left", "right", "right", "right", "right", "right", "right", "right", "right"]}
                filas={(reporte.por_operador ?? []).map((o) => [
                  o.nombre_operador,
                  numero(o.total),
                  numero(o.rbd_afectados),
                  numero(o.rbd_asignados),
                  porcentaje(o.porcentaje_parque),
                  numero(o.duracion_abarcada_horas, 2),
                  numero(o.mttr_promedio_horas, 2),
                  numero(o.en_tramite),
                  numero(o.rechazadas),
                ])}
              />
              <GraficoPorOperador reporte={reporte} />
            </Seccion>

            {(reporte.mapa_calor?.filas ?? []).length > 0 && (
              <Seccion
                titulo="RBD afectados por día y zona"
                nota={`Un establecimiento cuenta una vez por día aunque lo toquen varias contingencias. Solo entran los eventos no programados: las PROGRAMADA de esta base abarcan el mes completo y dejarían el mapa plano.${
                  reporte.mapa_calor.zonas_omitidas
                    ? ` Se muestran las ${reporte.mapa_calor.filas.length} zonas con más RBD afectados; quedaron fuera ${reporte.mapa_calor.zonas_omitidas}.`
                    : ""
                }`}
              >
                <GraficoMapaCalor reporte={reporte} />
              </Seccion>
            )}

            {(reporte.mapa_calor_porcentaje?.filas ?? []).length > 0 && (
              <Seccion
                titulo="Porcentaje de la zona afectado, por día"
                nota={`El mismo mapa anterior re-sombreado: mismas zonas, mismos días. Padrón de la zona: los establecimientos que la zona tiene registrados, sin distinguir operador. El numerador cruza los afectados contra ese padrón en vez de dividir dos totales, por la misma razón que el % de parque por operador: una zonal arrastra los establecimientos que la zona tenía al declararse y alguno puede figurar hoy en otra zona, así que dividiéndolos a secas la zona pasaría del 100%. Esos casos van en la columna "Fuera del padrón" y son un dato en sí mismos: una zona con muchos establecimientos fuera de su padrón indica una declaración que no calza con la zona que dice cubrir. Mientras esa columna esté en cero, el numerador es exactamente el conteo del mapa anterior. La escala de color llega hasta el máximo del período y no hasta el 100%: con un techo fijo, un mes cuyo peak es del 8% saldría entero casi blanco.${
                  reporte.mapa_calor_porcentaje.filas.some((f) => f.rbd_fuera_padron)
                    ? ` En este período ${
                        reporte.mapa_calor_porcentaje.filas.filter((f) => f.rbd_fuera_padron)
                          .length
                      } zona(s) tienen establecimientos alcanzados fuera de su padrón: en esas filas un día puede quedar en blanco aunque el mapa anterior lo pinte, porque los establecimientos afectados ese día no pertenecen al padrón de la zona.`
                    : ""
                }${
                  reporte.mapa_calor_porcentaje.zonas_omitidas
                    ? ` Las zonas visibles son las del mapa anterior, elegidas por cantidad de establecimientos afectados; quedaron fuera ${reporte.mapa_calor_porcentaje.zonas_omitidas}.`
                    : ""
                }${
                  (reporte.mapa_calor_porcentaje.maximo_omitido ?? 0) >
                  (reporte.mapa_calor_porcentaje.maximo ?? 0)
                    ? ` La más afectada en proporción de las que quedaron fuera llegó a ${porcentaje(
                        reporte.mapa_calor_porcentaje.maximo_omitido
                      )} en su peor día, sobre el máximo de este cuadro: son zonas chicas, con pocos establecimientos afectados pero alta incidencia sobre su padrón.`
                    : ""
                }${
                  reporte.mapa_calor_porcentaje.zonas_sin_padron
                    ? ` ${reporte.mapa_calor_porcentaje.zonas_sin_padron} zonas aparecen sin datos por no tener establecimientos en el padrón; la fila se conserva para que los dos mapas coincidan.`
                    : ""
                }${
                  reporte.id_operador
                    ? " El reporte está filtrado por operador: el numerador solo cuenta los establecimientos de ese operador mientras el denominador sigue siendo la zona completa, así que estos porcentajes quedan por debajo de los del reporte sin filtro."
                    : ""
                }`}
              >
                <GraficoMapaCalorPorcentaje reporte={reporte} />
                <Tabla
                  cabeceras={["Zona", "Padrón de la zona", "Afectados del padrón", "% de la zona", "Día peak", "% en el peak", "Fuera del padrón"]}
                  alineaciones={["left", "right", "right", "right", "right", "right", "right"]}
                  filas={reporte.mapa_calor_porcentaje.filas.map((f) => [
                    f.nombre_zona,
                    numero(f.rbd_padron),
                    numero(f.rbd_afectados),
                    porcentaje(f.porcentaje_mes),
                    numero(f.dia_peak),
                    porcentaje(f.peak),
                    numero(f.rbd_fuera_padron),
                  ])}
                />
              </Seccion>
            )}

            <Seccion titulo="Mayor impacto efectivo">
              <Tabla
                cabeceras={["ID", "Tipo", "Operador", "Estado", "RBD", "Duración (h)", "Efectivo (h-RBD)", "MTTR (h)"]}
                alineaciones={["right", "left", "left", "left", "right", "right", "right", "right"]}
                filas={(reporte.detalle?.mayor_impacto ?? []).map((d) => [
                  d.id,
                  d.tipo,
                  d.nombre_operador,
                  d.estado,
                  numero(d.rbd_afectados),
                  numero(d.duracion_abarcada_horas, 2),
                  numero(d.impacto_efectivo_rbd_horas, 2),
                  numero(d.mttr_horas, 2),
                ])}
              />
            </Seccion>

            {(reporte.detalle?.en_tramite_antiguas ?? []).length > 0 && (
              <Seccion
                titulo="Más antiguas sin cerrar"
                nota="Antigüedad desde la última transición de estado; si no hay historial, desde la fecha de creación."
              >
                <Tabla
                  cabeceras={["ID", "Tipo", "Operador", "Estado (etapa)", "RBD", "Antig. (d)", "Duración (h)"]}
                  alineaciones={["right", "left", "left", "left", "right", "right", "right"]}
                  filas={reporte.detalle.en_tramite_antiguas.map((d) => [
                    d.id,
                    d.tipo,
                    d.nombre_operador,
                    d.estado,
                    numero(d.rbd_afectados),
                    numero(d.antiguedad_etapa_dias, 2),
                    numero(d.duracion_abarcada_horas, 2),
                  ])}
                />
              </Seccion>
            )}
          </PageBgBlock>
        )}
      </Box>
    </PageBody>
  );
}
